import React, { useState, useEffect, useContext, useRef } from "react";
import * as XLSX from "xlsx";
import { useForm } from "react-hook-form";
import { usePostHog } from 'posthog-js/react';
import { captureEvent, captureError } from "../utils/posthogUtils";
import { POSTHOG_EVENTS } from "../constants/posthog-events";
import "./ModalStyles.css";
import "./BulkUploadModal.css";
import { NotificationsContext } from "../context/NotificationsContext";
import axiosInstance from "../api/axiosInstance";
import CustomUpload from "./Custom/CustomUpload";
import CustomButton from "./Custom/CustomButton";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Alert, Stack, Typography } from "@mui/material";
import { formatToDDMMYYYY } from "../utils/taskUtils";
import { toast } from "react-toastify";

// ── Helpers ───────────────────────────────────────────────────────────────────

const splitAssigneeInput = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => splitAssigneeInput(item)).filter(Boolean);
  }
  if (value === null || value === undefined) return [];
  if (typeof value !== "string") {
    const str = String(value).trim();
    return str ? [str] : [];
  }
  return value.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);
};

const normalizeHeaderKey = (header) => {
  if (header === null || header === undefined) return "";
  return header
    .toString()
    .toLowerCase()
    .replace(/\(.*?\)/g, " ")
    .replace(/[^a-z0-9@]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
};

const formatDateObject = (date) => formatToDDMMYYYY(date);

const toDDMMYYYY = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "number") {
    const millis = (value - 25569) * 86400 * 1000;
    return formatDateObject(new Date(millis)) || String(value);
  }
  if (value instanceof Date) return formatDateObject(value);
  const str = value.toString().trim();
  if (!str) return "";
  const isoMatch = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    const [, yyyy, mm, dd] = isoMatch;
    return `${dd.padStart(2, "0")}/${mm.padStart(2, "0")}/${yyyy}`;
  }
  const dayFirstMatch = str.match(/^([0-3]?\d)[-\/.]([0-1]?\d)[-\/.](\d{2,4})$/);
  if (dayFirstMatch) {
    let [, dd, mm, yy] = dayFirstMatch;
    dd = dd.padStart(2, "0");
    mm = mm.padStart(2, "0");
    if (yy.length === 2) {
      const numericYear = parseInt(yy, 10);
      yy = numericYear >= 50 ? `19${yy}` : `20${yy}`;
    }
    return `${dd}/${mm}/${yy}`;
  }
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) return formatDateObject(parsed);
  return str;
};

const excelSerialToDDMMYYYY = (serial) => {
  if (typeof serial !== "number") return toDDMMYYYY(serial);
  const millis = (serial - 25569) * 86400 * 1000;
  return formatDateObject(new Date(millis)) || String(serial);
};

// ── Component ─────────────────────────────────────────────────────────────────

const BulkUploadModal = ({ open, onClose, onBulkUploadSuccess }) => {
  const { addNotification } = useContext(NotificationsContext);
  const posthog = usePostHog();
  const alertRef = useRef(null);

  const { control, watch, reset } = useForm({
    defaultValues: { file: [] },
    shouldFocusError: true
  });
  const watchedFiles = watch("file");

  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadErrors, setUploadErrors] = useState([]);

  // Scroll to error when it appears
  useEffect(() => {
    if (error && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  // Parse file whenever CustomUpload changes the form field
  useEffect(() => {
    const file = Array.isArray(watchedFiles) ? watchedFiles[0] : null;
    if (!file) {
      setPreviewData([]);
      setError("");
      setSuccess("");
      return;
    }

    if (file.name.toLowerCase().endsWith(".csv")) {
      setError("CSV files are not allowed. Please upload an Excel file (.xlsx or .xls).");
      setPreviewData([]);
      return;
    }
    setError("");
    setSuccess("");
    setPreviewData([]);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        setPreviewData(processExcelData(jsonData));
      } catch {
        setError("Error reading file. Please check the file format.");
      }
    };
    reader.readAsArrayBuffer(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedFiles]);

  const processExcelData = (jsonData) => {
    if (jsonData.length < 2) {
      setError("File must contain at least a header row and one data row");
      return [];
    }
    const headers = jsonData[0];
    const dataRows = jsonData.slice(1);
    return dataRows.map((row, index) => {
      const task = {};
      headers.forEach((header, colIndex) => {
        const value = row[colIndex] || "";
        const headerKey = normalizeHeaderKey(header);
        switch (headerKey) {
          case "title":
          case "task_name":
          case "taskname":
            task.title = value; break;
          case "description":
          case "desc":
            task.description = value; break;
          case "assigned_email":
          case "assignee_email":
          case "email":
          case "assigned_to":
          case "assigned_to_emails":
          case "assigned_to_email":
          case "assigned_to_emails_comma_separated":
          case "assignedto":
          case "employee":
          case "employee_name":
            task.assigned_to_raw = value;
            task.assigned_to_list = splitAssigneeInput(value);
            break;
          case "due_date":
          case "target_date":
          case "targetdate":
          case "target_completion_date":
          case "target_date_dd_mm_yyyy":
          case "duedate":
            task.due_date = typeof value === "number" ? excelSerialToDDMMYYYY(value) : toDDMMYYYY(value); break;
          case "start_date":
          case "startdate":
          case "start_date_dd_mm_yyyy":
            task.start_date = typeof value === "number" ? excelSerialToDDMMYYYY(value) : toDDMMYYYY(value); break;
          case "task_type":
          case "task type":
          case "tasktype":
          case "type":
          case "priority":
            task.priority = value; break;
          case "project":
            task.project = value; break;
          case "department":
          case "department_id":
            task.department = value; break;
          case "status":
            task.status = value || "Pending"; break;
          case "kpi":
            task.kpi = value; break;
          default: break;
        }
      });
      const assignedList = task.assigned_to_list || [];
      const assignedDisplay = assignedList.join(", ");
      return {
        ...task,
        assigned_to: assignedDisplay || task.assigned_to_raw || "",
        assigned_to_display: assignedDisplay || task.assigned_to_raw || "",
        start_date: task.start_date || "",
        rowIndex: index + 2,
        isValid: Boolean(task.title) &&
          Array.isArray(task.assigned_to_list) &&
          task.assigned_to_list.length > 0 &&
          Boolean(task.due_date),
      };
    });
  };

  const applyUploadResult = (result, file, validTasks, isAxiosError = false) => {
    const errorsBlock = result?.errors || {};
    const created = errorsBlock.created ?? result?.data?.created ?? result?.createdCount ?? 0;
    const failed = errorsBlock.failed ?? result?.data?.failed ?? result?.errorCount ?? 0;
    const rowErrors = Array.isArray(errorsBlock.errors) ? errorsBlock.errors : [];

    if (rowErrors.length > 0) setUploadErrors(rowErrors);

    // If it's an axios error or we have row-level failures, it's not a 'success'
    const hasFailures = isAxiosError || failed > 0 || rowErrors.length > 0;

    if (!hasFailures) {
      // ── All tasks succeeded ───────────────────────────────────────────────
      setUploadProgress(100);
      setSuccess(`✅ ${result?.message || "Tasks uploaded successfully."}`);
      try {
        addNotification({
          type: "task_created",
          title: "Bulk upload complete",
          message: result?.message || "All tasks created successfully.",
          meta: { created, errors: 0 },
        });
      } catch { }
      captureEvent(posthog, POSTHOG_EVENTS.BULK_UPLOAD_COMPLETED, {
        task_count: created, error_count: 0, file_name: file?.name,
      });
      // Keep loading true during the redirect timeout
      setTimeout(() => {
        if (onBulkUploadSuccess) onBulkUploadSuccess();
        handleClose();
      }, 3000);
    } else if (created > 0) {
      // ── Partial success ───────────────────────────────────────────────────
      setUploadProgress(100);
      setLoading(false);
      // Construct a clear message
      const downloadHint = rowErrors.length > 0 ? " Download the error report below." : "";
      const msg = `⚠️ ${created} task(s) created. ${failed} row(s) failed.${downloadHint}`;
      setError(msg);
      toast.warning(msg);

      try {
        addNotification({
          type: "task_created",
          title: "Bulk upload partial",
          message: `${created} task(s) created, ${failed} error(s).`,
          meta: { created, errors: failed },
        });
      } catch { }
      captureEvent(posthog, POSTHOG_EVENTS.BULK_UPLOAD_COMPLETED, {
        task_count: created, error_count: failed, file_name: file?.name,
      });
    } else {
      // ── All rows failed ───────────────────────────────────────────────────
      setLoading(false);
      const msg = result?.message || "Upload failed.";
      const downloadHint = rowErrors.length > 0 ? " Download the error report below." : "";
      const errorMsg = `❌ ${msg}${downloadHint}`;
      setError(errorMsg);
      toast.error(errorMsg);
      captureError(posthog, POSTHOG_EVENTS.BULK_UPLOAD_FAILED, new Error(msg), { task_count: validTasks?.length });
    }
  };

  const handleUpload = async () => {
    const file = Array.isArray(watchedFiles) ? watchedFiles[0] : null;
    if (!file || previewData.length === 0) {
      setError("Please select a file and ensure it contains valid data");
      return;
    }
    const validTasks = previewData.filter((task) => task.isValid);
    if (validTasks.length === 0) {
      setError("No valid tasks found. Please check your data format.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    setUploadErrors([]);
    setUploadProgress(0); // Start at 0

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("tasks", JSON.stringify(validTasks));
      const { data: result } = await axiosInstance.post("/api/task/BulkUpload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          // Don't set to 100 immediately to leave room for backend processing
          setUploadProgress(Math.min(percentCompleted, 95));
        },
      });
      applyUploadResult(result, file, validTasks);
    } catch (err) {
      // axiosInstance rejects non-2xx (e.g. 400, 422)
      const responseData = err?.originalError?.response?.data || err?.data;
      if (responseData) {
        applyUploadResult(responseData, file, validTasks, true);
      } else {
        const fallbackMessage = err?.message && !/NetworkError/i.test(err.message)
          ? err.message
          : "Network error. Please try again.";
        captureError(posthog, POSTHOG_EVENTS.BULK_UPLOAD_FAILED, err, { task_count: validTasks?.length });
        setError(fallbackMessage);
        toast.error(fallbackMessage);
        setLoading(false);
      }
    }
  };

  const downloadErrors = () => {
    const rows = uploadErrors.map((e) => ({
      "Row #": e.rowNumber ?? "",
      "Title": e.data?.title ?? "",
      "Description": e.data?.description ?? "",
      "Assigned_To_Emails (comma separated)": e.data?.emails ?? "",
      "Target_Date (DD-MM-YYYY)": e.data?.due_date ?? "",
      "Start_Date (DD-MM-YYYY)": e.data?.start_date ?? "",
      "Priority": e.data?.priority ?? "",
      "Project": e.data?.project_name ?? "",
      "Department": e.data?.department_name ?? "",
      "Status": e.data?.status ?? "",
      "KPI": e.data?.kpi ?? "",
      "Error Reason": e.reason ?? "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Upload Errors");
    XLSX.writeFile(wb, "bulk_upload_errors.xlsx");
  };

  const handleClose = () => {
    reset({ file: [] });
    setPreviewData([]);
    setError("");
    setSuccess("");
    setUploadProgress(0);
    setUploadErrors([]);
    setLoading(false);
    onClose();
  };

  const downloadTemplate = () => {
    captureEvent(posthog, POSTHOG_EVENTS.BULK_UPLOAD_TEMPLATE_DOWNLOADED);
    const template = [
      [
        "Title",
        "Description",
        "Assigned_To_Emails (comma separated)",
        "Start_Date (DD-MM-YYYY)",
        "Target_Date (DD-MM-YYYY)",
        "Priority",
        "Project",
        "Department",
        "Status",
        "KPI",
        "Estimated Hours",
        "Estimated Mins",
      ],
      [
        "Sample Task 1",
        "This is a sample task description",
        "johndoe@example.com, anita.sharma@vmart.co.in",
        new Date(2026, 4, 10),
        new Date(2026, 4, 15),
        "high",
        "Project A",
        "IT",
        "pending",
        "high",
        2,
        30,
      ],
    ];

    const ws = XLSX.utils.aoa_to_sheet(template, {
      cellDates: true, // 🔥 IMPORTANT
    });

    // Apply format
    const range = XLSX.utils.decode_range(ws["!ref"]);

    for (let R = 1; R <= range.e.r; ++R) {
      ["D", "E"].forEach((col) => {
        const cellRef = `${col}${R + 1}`;
        if (ws[cellRef] && ws[cellRef].v instanceof Date) {
          ws[cellRef].z = "dd-mm-yyyy"; // only date, no time
        }
      });
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tasks Template");

    XLSX.writeFile(wb, "task_bulk_upload_template.xlsx", {
      cellDates: true, // 🔥 IMPORTANT
    });
  };

  if (!open) return null;

  const currentFile = Array.isArray(watchedFiles) ? watchedFiles[0] : null;

  return (
    <div className="modal-overlay-base">
      <div
        className="modal-content-base"
        style={{ maxWidth: 800 }}
      >
        {/* Header */}
        <div className="modal-header-base">
          <h3 className="modal-title-base">Bulk Upload Tasks</h3>
          <button className="modal-close-base" onClick={handleClose} title="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body-base">
          <Stack spacing={3}>
            {/* File Upload */}
            <CustomUpload
              control={control}
              name="file"
              extensions={["xlsx", "xls"]}
              Icon={FileUploadOutlinedIcon}
              iconTittle="Upload Excel"
              maxFileSize={15728640}
            />

            {/* Template Download */}
            <div className="template-section">
              <button className="template-btn" onClick={downloadTemplate}>
                📥 Download Template
              </button>
              <p className="template-hint">
                Use our template to ensure correct format. Separate multiple assignee emails with
                commas and enter target/start dates as DD/MM/YYYY (or ISO YYYY-MM-DD). Leave start
                date blank if you only want a due date.
              </p>
            </div>

            {/* Alerts & Error Report */}
            <div ref={alertRef}>
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    borderRadius: "8px",
                    fontSize: "0.82rem",
                    alignItems: "center",
                    "& .MuiAlert-action": { padding: "0 0 0 16px" }
                  }}
                  action={
                    uploadErrors.length > 0 && (
                      <CustomButton
                        variant="contained"
                        size="small"
                        onClick={downloadErrors}
                        sx={{
                          textTransform: "none",
                          borderRadius: "6px",
                          whiteSpace: "nowrap",
                          backgroundColor: "#d97706",
                          "&:hover": { backgroundColor: "#b45309" },
                        }}
                      >
                        📥 Download Error Report
                      </CustomButton>
                    )
                  }
                >
                  {error}
                </Alert>
              )}
              {success && <Alert severity="success" sx={{ borderRadius: "8px", fontSize: "0.82rem" }}>{success}</Alert>}
            </div>

            {/* Preview Table */}
            {previewData.length > 0 && (
              <div className="preview-section">
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "#374151" }}>
                  Preview ({previewData.length} tasks — {previewData.filter((t) => t.isValid).length} valid)
                </Typography>
                <div className="preview-table-container">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Title</th>
                        <th>Assigned To</th>
                        <th>Start Date</th>
                        <th>Target Date</th>
                        <th>Status</th>
                        <th>Valid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((task, index) => (
                        <tr key={index} className={task.isValid ? "valid-row" : "invalid-row"}>
                          <td>{task.rowIndex}</td>
                          <td>{task.title || "Missing"}</td>
                          <td>{task.assigned_to || "Missing"}</td>
                          <td>{task.start_date || "—"}</td>
                          <td>{task.due_date || "Missing"}</td>
                          <td>{task.status || "Pending"}</td>
                          <td>
                            <span className={`validity-badge ${task.isValid ? "valid" : "invalid"}`}>
                              {task.isValid ? "✓" : "✗"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Progress Bar */}
            {loading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                </div>
                <p>Uploading tasks... {uploadProgress}%</p>
              </div>
            )}
          </Stack>
        </div>

        {/* Footer */}
        <div className="modal-footer-base" style={{ justifyContent: "flex-end", gap: 12 }}>
          <CustomButton
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            disabled={loading}
            sx={{ textTransform: "none", borderRadius: 2, minWidth: 100 }}
          >
            Cancel
          </CustomButton>
          <CustomButton
            variant="contained"
            color="primary"
            loading={loading}
            loadingText="Uploading..."
            disabled={!currentFile || previewData.length === 0}
            onClick={handleUpload}
            startIcon={<FileUploadOutlinedIcon />}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Upload Tasks
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
